/**
 * «Monitoraggio»: la maschera coach che risponde a chi si allena, con quale
 * continuita', che cosa era programmato e che cosa e' stato registrato.
 *
 * Un imbuto in tre livelli (D32, docs/monitoring/UX-REVIEW.md):
 *   1. la lista (`/monitoring`): due viste dello stesso livello, Panoramica e
 *      Registro giornate, ordinate per attenzione;
 *   2. l'atleta (`/monitoring/:athleteId`): una rotta sua, cosi' Indietro del
 *      browser torna alla lista e un giorno del registro si apre gia' in vista;
 *   3. un movimento, dentro «Andamenti» della scheda atleta.
 *
 * Periodo, gruppo, programma, vista e ricerca stanno nell'URL: scendendo e
 * risalendo il contesto resta quello scelto. Il periodo viaggia con la data
 * locale del coach (`today`), cosi' il server non decide il fuso.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
	AlertTriangle,
	BellRing,
	CalendarRange,
	ChevronDown,
	LayoutGrid,
	Loader2,
	RotateCcw,
	Search,
	SlidersHorizontal,
	Table2,
} from "lucide-react";
import { fetchAPI, formatFullName, getToday } from "../../lib/utils";
import { matchesCoachAthleteSearch } from "../../lib/coachUtils";
import { useLanguage } from "../../contexts/LanguageContext";
import {
	alertSettingsCustomized,
	PERIOD_PRESETS,
	periodRange,
	sortAthleteRows,
} from "../../lib/monitoringUtils";
import { Input } from "../ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import { TooltipProvider } from "../ui/tooltip";
import { Button } from "../ui/button";
import { MonitoringOverviewTable } from "./MonitoringOverviewTable";
import { AttendanceGrid } from "./AttendanceGrid";
import { AthleteMonitoringPanel } from "./AthleteMonitoringPanel";
import { useIsCompact } from "./primitives";
import { AlertSettingsDialog } from "./AlertSettingsDialog";

export const PERIOD_LABEL_KEYS = {
	"7d": "monPeriod7d",
	"14d": "monPeriod14d",
	"28d": "monPeriod28d",
	this_week: "monPeriodThisWeek",
	last_week: "monPeriodLastWeek",
};

// Gli stessi predefiniti del server (services/monitoring.py): servono solo a
// dire «personalizzati» sul bottone, la regola vera resta sul server.
const DEFAULT_ALERTS = {
	missing_logs: { enabled: true, min: 2 },
	inactive: { enabled: true, days: 10 },
	high_rpe: { enabled: true, threshold: 9, count: 2 },
	wellness_low: { enabled: true, fields: 2, max_age_days: 3 },
	wellness_gap: { enabled: true, days: 7 },
};

// I parametri dell'URL che appartengono solo alla scheda atleta: risalendo alla
// lista si tolgono, il resto (periodo, gruppo, programma, vista, ricerca) resta.
const ATHLETE_ONLY_PARAMS = ["day", "tab"];

const flaggedRow = (row) => (row.flags || []).some((flag) => flag.code !== "no_data");

function PeriodSelect({ value, onChange, t }) {
	return (
		<Select value={value} onValueChange={onChange}>
			<SelectTrigger className="rounded-sm" aria-label={t("monPeriod")}>
				<CalendarRange className="w-4 h-4 mr-2 text-muted-foreground" aria-hidden="true" />
				<SelectValue />
			</SelectTrigger>
			<SelectContent>
				{PERIOD_PRESETS.map((key) => (
					<SelectItem key={key} value={key}>
						{t(PERIOD_LABEL_KEYS[key])}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
}

function ProgramSelect({ value, onChange, programs, t, className = "" }) {
	const options =
		value !== "all" && !programs.some((program) => program.program_id === value)
			? [...programs, { program_id: value, title: value }]
			: programs;
	return (
		<Select value={value} onValueChange={onChange}>
			<SelectTrigger className={`rounded-sm ${className}`} aria-label={t("monProgramFilter")}>
				<SelectValue />
			</SelectTrigger>
			<SelectContent>
				<SelectItem value="all">{t("monAllPrograms")}</SelectItem>
				{options.map((program) => (
					<SelectItem key={program.program_id} value={program.program_id}>
						{program.title}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
}

/** Tre righe finte mentre arriva la prima risposta: mai «nessun atleta» prima di saperlo. */
function LoadingRows({ t }) {
	return (
		<div className="space-y-2" aria-busy="true" data-testid="mon-loading">
			<span className="sr-only">{t("loading")}</span>
			{[0, 1, 2].map((index) => (
				<div key={index} className="h-16 rounded-sm border border-border bg-muted/30 animate-pulse" />
			))}
		</div>
	);
}

/**
 * La sintesi del gruppo in una riga. L'unico numero con un'azione dietro, gli
 * atleti con avvisi, e' un bottone che filtra la lista e lo dice.
 */
function GroupSummary({ group, flagged, onlyFlagged, onToggleFlagged, programFiltered, t }) {
	return (
		<div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm" data-testid="mon-summary">
			<span>
				<span className="font-heading font-bold">{group.athletes}</span> {t("monSummaryAthletes")}
				<span className="text-muted-foreground"> · </span>
				<span className="font-heading font-bold">{group.with_activity}</span> {t("monSummaryWithActivity")}
				<span className="text-muted-foreground"> · </span>
				<span className="font-heading font-bold">
					{group.complete + group.partial}/{group.assigned_days}
				</span>{" "}
				{t("monSummaryDaysWithOutcome")}
			</span>
			{flagged || onlyFlagged ? (
				<Button
					type="button"
					variant="outline"
					size="sm"
					aria-pressed={onlyFlagged}
					onClick={onToggleFlagged}
					className={`rounded-sm h-8 ${
						onlyFlagged
							? "border-amber-400/60 bg-amber-400/15"
							: "border-amber-400/40 text-amber-700 dark:text-amber-400"
					}`}
					data-testid="mon-flagged-filter"
				>
					<AlertTriangle className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
					{flagged} {t("monFlaggedCount")} · {t(onlyFlagged ? "monFlaggedShowAll" : "monFlaggedShowOnly")}
				</Button>
			) : null}
			{programFiltered ? (
				<span className="text-xs text-muted-foreground">{t("monProgramFilterNote")}</span>
			) : null}
		</div>
	);
}

/** Cosa vale per tutti i numeri della pagina: su richiesta, non sopra la lista (H10). */
function AboutData({ t }) {
	return (
		<details className="text-xs text-muted-foreground" data-testid="mon-about-data">
			<summary className="cursor-pointer select-none">{t("monAboutData")}</summary>
			<p className="mt-1 max-w-3xl">{t("monAboutDataText")}</p>
		</details>
	);
}

export function MonitoringTab() {
	const { t, language } = useLanguage();
	const compact = useIsCompact();
	const navigate = useNavigate();
	const { athleteId } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	const today = useMemo(() => getToday(), []);

	const preset = PERIOD_PRESETS.includes(searchParams.get("period")) ? searchParams.get("period") : "14d";
	const groupId = searchParams.get("group") || "all";
	const programId = searchParams.get("program") || "all";
	const view = searchParams.get("view") === "register" ? "register" : "overview";
	const search = searchParams.get("q") || "";
	const legacyAthlete = searchParams.get("athlete");

	const [groups, setGroups] = useState([]);
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [alertsOpen, setAlertsOpen] = useState(false);
	const [onlyFlagged, setOnlyFlagged] = useState(false);
	// Sul telefono i filtri stanno in una riga che si apre: tre selettori in
	// colonna, sopra la lista, spingevano gli atleti sotto la piega.
	const [filtersOpen, setFiltersOpen] = useState(false);
	const [sort, setSort] = useState({ key: "attention", direction: "asc" });
	// I programmi della scheda atleta, per il filtro quando si arriva da un link diretto.
	const [athletePrograms, setAthletePrograms] = useState([]);

	const range = useMemo(() => periodRange(preset, today), [preset, today]);

	// I vecchi link `?athlete=` portano alla rotta della scheda.
	useEffect(() => {
		if (!athleteId && legacyAthlete) navigate(`/monitoring/${legacyAthlete}`, { replace: true });
	}, [athleteId, legacyAthlete, navigate]);

	/** Cambia un parametro dell'URL senza aggiungere un passo alla cronologia. */
	const setParam = useCallback(
		(key, value, fallback) => {
			setSearchParams(
				(current) => {
					const next = new URLSearchParams(current);
					if (value === undefined || value === null || value === "" || value === fallback) next.delete(key);
					else next.set(key, value);
					return next;
				},
				{ replace: true },
			);
		},
		[setSearchParams],
	);

	const listSearch = useMemo(() => {
		const next = new URLSearchParams(searchParams);
		ATHLETE_ONLY_PARAMS.forEach((key) => next.delete(key));
		next.delete("athlete");
		const text = next.toString();
		return text ? `?${text}` : "";
	}, [searchParams]);

	const openAthlete = (id, day) => {
		const next = new URLSearchParams(listSearch);
		if (day) next.set("day", day);
		const text = next.toString();
		navigate(`/monitoring/${id}${text ? `?${text}` : ""}`);
	};

	const backToList = () => navigate(`/monitoring${listSearch}`);

	useEffect(() => {
		fetchAPI("/groups")
			.then((rows) => setGroups(Array.isArray(rows) ? rows : []))
			.catch(() => setGroups([]));
	}, []);

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const params = new URLSearchParams({ date_from: range.from, date_to: range.to, today });
			if (groupId !== "all") params.set("group_id", groupId);
			if (programId !== "all") params.set("program_id", programId);
			const payload = await fetchAPI(`/coach/monitoring/overview?${params.toString()}`);
			setData(payload);
		} catch (err) {
			setError(err?.message || t("monLoadError"));
			toast.error(t("monLoadError"));
		} finally {
			setLoading(false);
		}
	}, [range.from, range.to, today, groupId, programId, t]);

	// La lista si legge solo quando la si guarda: la scheda atleta ha la sua rotta.
	useEffect(() => {
		if (!athleteId) load();
	}, [athleteId, load]);

	const rows = useMemo(
		() =>
			sortAthleteRows(
				(data?.athletes || []).filter(
					(row) => matchesCoachAthleteSearch(row, search) && (!onlyFlagged || flaggedRow(row)),
				),
				sort.key,
				sort.direction,
			),
		[data, search, sort, onlyFlagged],
	);

	// Stesso tasto: inverte il verso; un tasto nuovo parte dal verso piu' utile
	// (nome e attenzione crescenti, tutto il resto decrescente: prima chi ha di piu').
	const toggleSort = (key) =>
		setSort((current) =>
			current.key === key
				? { key, direction: current.direction === "asc" ? "desc" : "asc" }
				: { key, direction: key === "name" || key === "attention" ? "asc" : "desc" },
		);

	const customized = useMemo(
		() => alertSettingsCustomized(data?.alert_settings, DEFAULT_ALERTS),
		[data],
	);
	const flaggedAthletes = (data?.athletes || []).filter(flaggedRow).length;
	const programs = data?.programs?.length ? data.programs : athletePrograms;
	const groupName = groupId !== "all" ? groups.find((grp) => grp.group_id === groupId)?.name : null;
	const programTitle =
		programId !== "all" ? programs.find((program) => program.program_id === programId)?.title || null : null;
	const filterSummary = [t(PERIOD_LABEL_KEYS[preset]), groupName, programTitle].filter(Boolean).join(" · ");
	const rangeLabel = range.from === range.to ? range.from : `${range.from} → ${range.to}`;

	const header = (
		<div className="flex flex-wrap items-start justify-between gap-3">
			<div className="min-w-0">
				<h1 className="font-heading text-xl md:text-3xl font-bold uppercase tracking-tight">{t("monTitle")}</h1>
				<p className="hidden md:block text-sm text-muted-foreground max-w-3xl mt-1">{t("monSubtitle")}</p>
			</div>
			<div className="flex items-center gap-2">
				{loading && data ? (
					<Loader2 className="w-5 h-5 animate-spin text-muted-foreground" aria-label={t("loading")} />
				) : null}
				<Button
					variant="outline"
					className="rounded-sm"
					onClick={() => setAlertsOpen(true)}
					data-testid="monitoring-alerts-button"
				>
					<BellRing className="w-4 h-4 mr-1.5" aria-hidden="true" />
					{t("monAlertSettingsButton")}
					{data?.alert_settings && customized ? (
						<>
							<span className="hidden sm:inline ml-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
								{t("monAlertCustomized")}
							</span>
							<span
								className="sm:hidden ml-1.5 h-1.5 w-1.5 rounded-full bg-volt"
								aria-label={t("monAlertCustomized")}
							/>
						</>
					) : null}
				</Button>
			</div>
		</div>
	);

	// ── Livello 2: un atleta ───────────────────────────────────────────────
	if (athleteId) {
		return (
			<TooltipProvider delayDuration={150}>
				<div className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-4" data-testid="monitoring-page">
					<AthleteMonitoringPanel
						athleteId={athleteId}
						range={range}
						periodLabel={t(PERIOD_LABEL_KEYS[preset])}
						today={today}
						programId={programId}
						initialDay={searchParams.get("day")}
						tab={searchParams.get("tab") || "days"}
						onTabChange={(next) => setParam("tab", next, "days")}
						onLoaded={(payload) => setAthletePrograms(payload?.programs || [])}
						onBack={backToList}
						filters={
							<div className="grid grid-cols-2 gap-2 md:max-w-xl">
								<PeriodSelect value={preset} onChange={(value) => setParam("period", value, "14d")} t={t} />
								<ProgramSelect
									value={programId}
									onChange={(value) => setParam("program", value, "all")}
									programs={programs}
									t={t}
								/>
							</div>
						}
						t={t}
						language={language}
					/>
				</div>
			</TooltipProvider>
		);
	}

	// ── Livello 1: la lista ────────────────────────────────────────────────
	const searchField = (
		<div className="relative col-span-2 sm:col-span-1">
			<Search
				className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
				aria-hidden="true"
			/>
			<Input
				value={search}
				onChange={(event) => setParam("q", event.target.value, "")}
				placeholder={t("monSearchAthlete")}
				className="pl-8 rounded-sm"
				aria-label={t("monSearchAthlete")}
			/>
		</div>
	);

	return (
		<TooltipProvider delayDuration={150}>
			<div className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-4" data-testid="monitoring-page">
				{header}
				<AlertSettingsDialog open={alertsOpen} onOpenChange={setAlertsOpen} onSaved={load} t={t} />

				{compact ? (
					<button
						type="button"
						onClick={() => setFiltersOpen((open) => !open)}
						aria-expanded={filtersOpen}
						className="w-full flex items-center justify-between gap-2 rounded-sm border border-border bg-card px-3 h-10 text-sm"
						data-testid="mon-filters-toggle"
					>
						<span className="flex items-center gap-2 min-w-0">
							<SlidersHorizontal className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden="true" />
							<span className="truncate">{filterSummary}</span>
						</span>
						<ChevronDown
							className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${filtersOpen ? "rotate-180" : ""}`}
							aria-hidden="true"
						/>
					</button>
				) : null}

				{!compact || filtersOpen ? (
					<div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
						<PeriodSelect value={preset} onChange={(value) => setParam("period", value, "14d")} t={t} />
						<Select value={groupId} onValueChange={(value) => setParam("group", value, "all")}>
							<SelectTrigger className="rounded-sm" aria-label={t("monGroupFilter")}>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t("monAllGroups")}</SelectItem>
								{groups.map((grp) => (
									<SelectItem key={grp.group_id} value={grp.group_id}>
										{grp.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<ProgramSelect
							value={programId}
							onChange={(value) => setParam("program", value, "all")}
							programs={programs}
							t={t}
							className="col-span-2 sm:col-span-1"
						/>
						{compact ? null : searchField}
					</div>
				) : null}
				{compact ? searchField : null}

				<div className="space-y-1.5">
					<div className="text-xs text-muted-foreground">{rangeLabel}</div>
					{data?.group ? (
						<GroupSummary
							group={data.group}
							flagged={flaggedAthletes}
							onlyFlagged={onlyFlagged}
							onToggleFlagged={() => setOnlyFlagged((value) => !value)}
							programFiltered={programId !== "all"}
							t={t}
						/>
					) : null}
				</div>

				{error ? (
					<div className="flex flex-wrap items-center justify-between gap-2 rounded-sm border border-blaze/30 bg-blaze/10 px-4 py-3 text-sm text-blaze">
						<span>{error}</span>
						<Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={load}>
							<RotateCcw className="w-4 h-4 mr-1.5" aria-hidden="true" />
							{t("tryAgain")}
						</Button>
					</div>
				) : null}

				<Tabs value={view} onValueChange={(next) => setParam("view", next, "overview")}>
					<TabsList className="rounded-sm grid w-full grid-cols-2 md:inline-flex md:w-auto">
						<TabsTrigger value="overview" className="rounded-sm min-w-0">
							<Table2 className="hidden sm:block w-4 h-4 mr-1.5 shrink-0" aria-hidden="true" />
							<span className="truncate">{t("monViewOverview")}</span>
						</TabsTrigger>
						<TabsTrigger value="register" className="rounded-sm min-w-0">
							<LayoutGrid className="hidden sm:block w-4 h-4 mr-1.5 shrink-0" aria-hidden="true" />
							<span className="truncate">{t(compact ? "monViewAttendanceShort" : "monViewAttendance")}</span>
						</TabsTrigger>
					</TabsList>
				</Tabs>

				{!data && loading ? (
					<LoadingRows t={t} />
				) : (
					<div className={loading ? "opacity-60 transition-opacity" : "transition-opacity"}>
						{view === "overview" ? (
							<div className="space-y-2">
								<MonitoringOverviewTable
									rows={rows}
									t={t}
									language={language}
									onSelect={(id) => openAthlete(id)}
									onWrite={(row) =>
										navigate("/messages", { state: { contact: { user_id: row.athlete_id, name: formatFullName(row) } } })
									}
									sort={sort}
									onSort={toggleSort}
								/>
								{rows.length ? (
									<p className="text-xs text-muted-foreground">
										{t(compact ? "monOverviewHintTouch" : "monOverviewHint")}
									</p>
								) : null}
							</div>
						) : (
							<AttendanceGrid
								rows={rows}
								dates={data?.window?.dates || []}
								today={today}
								t={t}
								language={language}
								onSelectDay={(id, date) => openAthlete(id, date)}
							/>
						)}
					</div>
				)}
				<AboutData t={t} />
			</div>
		</TooltipProvider>
	);
}
